import type { OrganizationSchema } from '../types/organization.type';
import { baseURL } from './http.helper';

const ABSOLUTE_URL_RE = /^https?:\/\//i;

/** Turn API-relative asset paths into absolute URLs for <img src>. */
export function absolutizeOrganizationAssetUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (ABSOLUTE_URL_RE.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  const base = (baseURL ?? '').replace(/\/$/, '');
  if (!base) return trimmed;
  return trimmed.startsWith('/') ? `${base}${trimmed}` : `${base}/${trimmed}`;
}

function readLogoCandidate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

/** Resolve an organization's logo URL from top-level or nested `data` fields. */
export function resolveOrganizationLogoUrl(
  org: OrganizationSchema | null | undefined,
): string | null {
  if (!org) return null;

  const candidates = [
    org.logo_url,
    (org.data as Record<string, unknown> | undefined)?.logo_url,
    (org.data as Record<string, unknown> | undefined)?.logo,
    (org.data as Record<string, unknown> | undefined)?.organization_logo_url,
  ];

  for (const candidate of candidates) {
    const raw = readLogoCandidate(candidate);
    if (raw) return absolutizeOrganizationAssetUrl(raw);
  }

  return null;
}

/** Initials for org avatar fallback when no logo is available. */
export function getOrganizationInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
  }

  return trimmed.charAt(0).toUpperCase();
}
